// ── docs · start here ───────────────────────────────────────────────────────
// The one page that shows the whole loop before any of its parts.

import type { DocPageDef } from '../content'

export const START_PAGES: DocPageDef[] = [
  {
    slug: 'how-it-works',
    title: 'how it works',
    group: 'start here',
    description: 'the whole loop in one page: capture, fold, retrieve, and the person in the middle of it.',
    updated: '2026-09-29',
    body: `
innernet is three things working as one: **a memory** at innernet.live, **netti**, who keeps it, and **a door** every AI tool walks through. Here is the loop, from a sentence in a chat to an answer in a different tool a week later.

## 1. something worth keeping is said

You are in Claude, Cursor, ChatGPT or a terminal. A decision gets made, a preference comes up, a commit lands. The connected AI notices, guided by the project's [capture protocol](/docs/capture-protocol), and calls one of:

- \`innernet_capture\` for the project ("we're going usage-based on pricing")
- \`innernet_self_capture\` for you ("prefers short answers, works in TypeScript")
- nothing at all, when it isn't worth keeping. Most things aren't.

In a repo, the git hooks do this for you on every commit and pull. See [repo memory](/docs/repo-memory).

A capture is **fast and dumb on purpose**: it lands in the commit log in milliseconds, raw, before anything interprets it. A routing failure can never lose it.

## 2. netti folds it in

On the server, netti's fold takes the captures since last time and consolidates them into the memory's **dimensions**: the pages that describe the project (\`vision\`, \`pricing\`, \`technical\`…). The fold replaces what was superseded rather than stacking it, and writes one dated line under \`## history\` so nothing is lost. Personal captures fold into **facts**, each with a disclosure class and every source it came from.

Every fold is a commit. Your dashboard's **inbox** shows what each fold did (a new fact, a changed one, a line you said), and your memory's size in **cells** grows with it. See [the inbox & the fold](/docs/inbox-and-fold).

## 3. any tool asks

A week later, a different tool, a fresh session. The AI calls \`innernet_context\` with your question in your words. innernet compiles a **budgeted slice**: the few sections that answer it (about 1,600 tokens at most, however big the memory), a few near-misses, the map by name, and the personal facts that bear on it, if any are shareable. See [retrieval](/docs/retrieval).

The answer builds on what was decided, and you never re-explained a thing.

## 4. you stay in charge

- **See it.** The [dashboard](/docs/dashboard) shows every memory, every page, every fact about you, and where each came from.
- **Shape it.** Tune the capture protocol, edit a page inline, move a fact's disclosure class.
- **Fork it.** [Branch](/docs/branches) a memory to try a direction, merge what wins.
- **Share it.** One page as a link, a whole memory as a room, a skill to the community. [Sharing](/docs/sharing)
- **Take it back.** [Forget](/docs/forgetting) a page, a fact, or everything, in two steps.

## what never happens

- **No model runs on your machine.** \`npx innernet\` wires config and hooks; every model call happens server-side, through one chokepoint.
- **No local copy.** Nothing to sync, nothing to go stale. Every tool reads the same memory.
- **No private fact leaves without you.** \`private\` and \`self\` facts stay inside innernet unless you allow an app to read them, and every time one leaves it is written down where you can see it.
- **No key in a chat.** Clients sign in through your browser over OAuth 2.1. An AI asking you to paste a key is wrong; hand it the URL.

## where to go next

- [Get started in two minutes](/docs/getting-started)
- [The starter guide, with pictures](/community/guides/starter-meet-innernet)
- [The Context Map model](/docs/concepts)
`,
  },
]
